import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { comicList } from '@/mocks/comics';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEO from '@/components/base/SEO';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();

  const comic = useMemo(() => comicList.find((c) => c.id === id), [id]);

  if (!comic) {
    return (
      <main className="min-h-screen pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
          <p className="text-foreground-600">漫画不存在或已下架</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm mt-3 inline-block whitespace-nowrap">返回首页</Link>
        </div>
      </main>
    );
  }

  const relatedComics = comicList
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 6);

  return (
    <main className="min-h-screen pt-16">
      <SEO
        title={`${comic.title} - 漫画免费韩漫在线阅读_韩漫画网`}
        description={`${comic.title} - 韩漫画网免费韩国漫画大全，${comic.author}作品，${comic.status}。${comic.description.slice(0, 100)}... 支持高清韩漫在线阅读。`}
        keywords={`漫画免费韩漫,${comic.title},${comic.author},韩漫在线阅读,免费韩国漫画大全,${comic.tags.join(',')}`}
        canonicalPath={`/comic/${comic.id}`}
        ogType="article"
        schemaJson={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: comic.title,
          author: { '@type': 'Person', name: comic.author },
          datePublished: comic.updateDate,
          image: comic.coverImage,
          description: comic.description,
          publisher: {
            '@type': 'Organization',
            name: '韩漫画网',
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://hanmanhua.com/comic/${comic.id}`,
          },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanmanhua.com/' },
              { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'https://hanmanhua.com/category/all' },
              { '@type': 'ListItem', position: 3, name: comic.category === 'love' ? '恋爱' : comic.category === 'fantasy' ? '奇幻' : comic.category === 'action' ? '动作' : comic.category === 'suspense' ? '悬疑' : comic.category === 'comedy' ? '搞笑' : comic.category === 'drama' ? '剧情' : '耽美', item: `https://hanmanhua.com/category/${comic.category}` },
              { '@type': 'ListItem', position: 4, name: comic.title },
            ],
          },
        }}
      />
      <article className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '漫画分类', href: '/category/all' },
            { label: comic.category === 'love' ? '恋爱' : comic.category === 'fantasy' ? '奇幻' : comic.category === 'action' ? '动作' : comic.category === 'suspense' ? '悬疑' : comic.category === 'comedy' ? '搞笑' : comic.category === 'drama' ? '剧情' : '耽美', href: `/category/${comic.category}` },
            { label: comic.title },
          ]}
        />

        <div className="flex flex-col lg:flex-row gap-6 md:gap-8 mt-4">
          {/* Cover */}
          <div className="flex-shrink-0 w-full sm:w-[280px]">
            <div className="aspect-[3/4] overflow-hidden rounded-xl bg-background-200">
              <img
                src={comic.coverImage}
                alt={comic.title}
                title={`${comic.title} - 漫画免费韩漫在线阅读`}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">{comic.title}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <span className="text-sm text-foreground-600">作者：{comic.author}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                comic.status === '连载中'
                  ? 'bg-accent-100 text-accent-700'
                  : 'bg-foreground-200 text-foreground-700'
              }`}>
                {comic.status}
              </span>
              <span className="text-sm text-accent-500 flex items-center gap-0.5">
                <i className="ri-star-fill"></i>
                {comic.rating} 分
              </span>
              <span className="text-sm text-foreground-500">
                <i className="ri-eye-line mr-1"></i>
                {(comic.views / 10000).toFixed(0)} 万阅读
              </span>
              <span className="text-sm text-foreground-500">
                更新于 {comic.updateDate}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-4">
              {comic.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-5 p-4 md:p-5 bg-background-100 rounded-xl">
              <h2 className="text-sm font-semibold text-foreground-950 mb-2">作品简介</h2>
              <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-5">
              <button className="flex-1 sm:flex-none px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full text-sm transition-all whitespace-nowrap cursor-pointer">
                <i className="ri-play-circle-line mr-2"></i>
                开始阅读
              </button>
              <button className="flex-1 sm:flex-none px-8 py-3 bg-secondary-500 hover:bg-secondary-600 text-white font-medium rounded-full text-sm transition-all whitespace-nowrap cursor-pointer">
                <i className="ri-bookmark-line mr-2"></i>
                加入书架
              </button>
            </div>
          </div>
        </div>

        {/* Chapters */}
        <section className="mt-10">
          <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">章节列表</h2>
          <div className="bg-background-50 border border-background-200/70 rounded-xl divide-y divide-background-200/70">
            {comic.chapters.map((ch, i) => (
              <div
                key={ch.id}
                className="flex items-center justify-between px-4 md:px-5 py-4 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-xs text-foreground-400 flex-shrink-0">{comic.chapters.length - i}</span>
                  <span className="text-sm text-foreground-800 truncate">{ch.title}</span>
                </div>
                <span className="text-xs text-foreground-400 flex-shrink-0 ml-3">{ch.updateDate}</span>
              </div>
            ))}
            {comic.chapters.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-foreground-400">
                暂无章节
              </div>
            )}
          </div>
        </section>

        {/* Related */}
        {relatedComics.length > 0 && (
          <section className="mt-12">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
              {relatedComics.map((rc) => (
                <Link
                  key={rc.id}
                  to={`/comic/${rc.id}`}
                  className="group block cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200">
                    <img
                      src={rc.coverImage}
                      alt={rc.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="mt-2">
                    <h3 className="text-sm font-medium text-foreground-950 line-clamp-1 group-hover:text-primary-500 transition-colors">
                      {rc.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-foreground-500">{rc.author}</span>
                      <span className="text-xs text-accent-500 flex items-center gap-0.5">
                        <i className="ri-star-fill text-[10px]"></i>
                        {rc.rating}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </main>
  );
}